// Re-export compensation types
export * from './types/compensation';

export type DealStatus = "qualified" | "pre-negotiation" | "negotiation" | "closed" | "paid" | "lost";

export type TimeFilter = "all" | "30days" | "90days";

export interface Deal {
  id: string;
  name: string;
  status: DealStatus;
  dealValue: number;
  commissionValue: number;
  commissionPercentage: number;
  createdAt: string; // YYYY-MM-DD
  updatedAt?: string; // ISO string
  company?: string;
  website?: string;
  contactName?: string;
  email?: string;
  phone?: string;
  partnerId?: string;
  billingCycle?: string;
  closeProbability?: number;
  expectedCloseDate?: string;
  products?: DealProduct[];
  clientHubId?: string;
  syncStatus?: 'synced' | 'pending' | 'failed' | 'not_synced';
  salesGoal?: number;
}

export interface Activity {
  id: string;
  dealId: string;
  partnerId?: string;
  type: 'note' | 'stage_change' | 'creation' | 'update' | 'call' | 'email' | 'meeting';
  content: string;
  createdAt: string;
}

export interface ProductPrice {
  id: string;
  productId: string;
  label?: string;
  price: number;
  recurringInterval: string;
}

export interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  recurringInterval: string;
  createdAt: string;
  prices?: ProductPrice[];
}

export interface DealProduct {
  dealId: string;
  productId: string;
  quantity: number;
  priceAtDeal: number;
  product?: Product;
}

// ============================================
// FUNDRAISING TYPES
// ============================================

export type FundStage = 
  | 'outreach' 
  | 'screening' 
  | 'call' 
  | 'partner_meeting' 
  | 'full_meeting' 
  | 'negotiation' 
  | 'terms_received' 
  | 'uninterested';

export type FundPriority = 'tier_1' | 'tier_2' | 'tier_3';

export type FundraiseStatus = 'draft' | 'active' | 'paused' | 'closed';

export type TermSheetStatus = 'received' | 'reviewing' | 'negotiating' | 'accepted' | 'declined' | 'expired';

export type FundraisingActivityType = 
  | 'note' 
  | 'email' 
  | 'call' 
  | 'meeting' 
  | 'pitch'
  | 'stage_change' 
  | 'term_sheet_received' 
  | 'document_sent'
  | 'vdr_access_granted' 
  | 'vdr_visit';

export interface Fundraise {
  id: string;
  name: string;
  description?: string;
  status: FundraiseStatus;
  raiseGoal?: number;
  linkedVdrId?: string;
  linkedVdr?: {
    id: string;
    name: string;
    slug: string;
  };
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FundraisingFund {
  id: string;
  fundraiseId: string;
  name: string;
  website?: string;
  logoUrl?: string;
  thesis?: string;
  typicalTicketMin?: number;
  typicalTicketMax?: number;
  portfolioCompanies?: string[];
  stage: FundStage;
  priority: FundPriority;
  notes?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  // Joined data
  contacts?: FundraisingContact[];
  termSheets?: FundraisingTermSheet[];
  activities?: FundraisingActivity[];
}

export interface FundraisingContact {
  id: string;
  fundId: string;
  name: string;
  title?: string;
  email?: string;
  phone?: string;
  linkedinUrl?: string;
  isPrimary: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FundraisingTermSheet {
  id: string;
  fundId: string;
  investmentAmount: number;
  preMoneyValuation?: number;
  liquidationPreference: number;
  participationCap?: number;
  boardSeats: number;
  proRataRights: boolean;
  antiDilution?: 'broad_based' | 'narrow_based' | 'full_ratchet' | 'none';
  status: TermSheetStatus;
  receivedAt: string;
  expiresAt?: string;
  documentUrl?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FundraisingActivity {
  id: string;
  fundId: string;
  contactId?: string;
  contact?: FundraisingContact;
  contacts?: FundraisingContact[];
  type: FundraisingActivityType;
  content: string;
  metadata?: Record<string, any>;
  createdBy?: string;
  createdAt: string;
}

export interface FundraisingAnalytics {
  totalFunds: number;
  activeFunds: number;
  stageCounts: Record<FundStage, number>;
  priorityCounts: Record<FundPriority, number>;
  totalTermSheets: number;
  committedCapital: number;
  pendingCapital: number;
  raiseGoal: number;
  oversubscriptionPct: number;
}

// Cap Table Types
export type CapTableEntryType = 'founder' | 'employee' | 'investor' | 'esop' | 'advisor' | 'other';

export interface CapTableEntry {
  id: string;
  fundraiseId: string;
  name: string;
  type: CapTableEntryType;
  ownershipPercentage: number;
  shares?: number;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const CAP_TABLE_TYPES: { type: CapTableEntryType; label: string; color: string }[] = [
  { type: 'founder', label: 'Founder', color: 'bg-blue-500' },
  { type: 'employee', label: 'Employee', color: 'bg-indigo-500' },
  { type: 'investor', label: 'Previous Investor', color: 'bg-purple-500' },
  { type: 'esop', label: 'ESOP Pool', color: 'bg-amber-500' },
  { type: 'advisor', label: 'Advisor', color: 'bg-teal-500' },
  { type: 'other', label: 'Other', color: 'bg-slate-500' },
];

// Helper function to calculate term sheet metrics (frontend)
export const calculateTermSheetMetrics = (investmentAmount: number, preMoneyValuation: number) => {
  const postMoneyValuation = preMoneyValuation + investmentAmount;
  const dilutionPercentage = postMoneyValuation > 0 
    ? (investmentAmount / postMoneyValuation) * 100 
    : 0;
  const ownershipPercentage = dilutionPercentage;

  return {
    postMoneyValuation,
    dilutionPercentage,
    ownershipPercentage
  };
};

// Stage display configuration
export const FUND_STAGES: { stage: FundStage; label: string; color: string; bgColor: string; description?: string }[] = [
  { stage: 'outreach', label: 'Outreach', color: 'text-slate-600', bgColor: 'bg-slate-50', description: 'Funds that have been contacted but haven\'t engaged yet.' },
  { stage: 'screening', label: 'Screening', color: 'text-amber-600', bgColor: 'bg-amber-50', description: 'Initial review or conversation to determine fit.' },
  { stage: 'call', label: 'Call', color: 'text-blue-600', bgColor: 'bg-blue-50', description: 'Scheduled call with an analyst or associate.' },
  { stage: 'partner_meeting', label: 'Partner Meeting', color: 'text-indigo-600', bgColor: 'bg-indigo-50', description: 'Meeting with a partner at the fund.' },
  { stage: 'full_meeting', label: 'Full Meeting', color: 'text-purple-600', bgColor: 'bg-purple-50', description: 'Presentation to the full partnership.' },
  { stage: 'negotiation', label: 'Negotiation', color: 'text-pink-600', bgColor: 'bg-pink-50', description: 'Discussion of terms and conditions.' },
  { stage: 'terms_received', label: 'Terms Received', color: 'text-emerald-600', bgColor: 'bg-emerald-50', description: 'Term sheet received.' },
];

export const FUND_PRIORITIES: { priority: FundPriority; label: string; color: string }[] = [
  { priority: 'tier_1', label: 'Tier 1', color: 'bg-amber-400' },
  { priority: 'tier_2', label: 'Tier 2', color: 'bg-slate-400' },
  { priority: 'tier_3', label: 'Tier 3', color: 'bg-slate-300' },
];

// Term Sheet status configuration
export const TERM_SHEET_STATUSES: { status: TermSheetStatus; label: string; color: string; bgColor: string }[] = [
  { status: 'received', label: 'Received', color: 'text-blue-600', bgColor: 'bg-blue-50' },
  { status: 'reviewing', label: 'Reviewing', color: 'text-amber-600', bgColor: 'bg-amber-50' },
  { status: 'negotiating', label: 'Negotiating', color: 'text-purple-600', bgColor: 'bg-purple-50' },
  { status: 'accepted', label: 'Accepted', color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
  { status: 'declined', label: 'Declined', color: 'text-red-600', bgColor: 'bg-red-50' },
  { status: 'expired', label: 'Expired', color: 'text-neutral-500', bgColor: 'bg-neutral-50' },
];

// Term Sheet Settings
export interface TermSheetSettings {
  defaultLiquidationPreference: number;
  defaultProRataRights: boolean;
  defaultAntiDilution: 'broad_based' | 'narrow_based' | 'full_ratchet' | 'none';
  defaultBoardSeats: number;
  requirePreMoneyValuation: boolean;
  requireDocumentUrl: boolean;
  enableNotifications: boolean;
  notificationEmail?: string;
  expirationDays?: number;
}

// ============================================
// HOLDINGS TYPES
// ============================================

export type HoldingsCompanyStatus = 'active' | 'inactive' | 'sold';
export type HoldingsSubscriptionStatus = 'active' | 'cancelled' | 'pending' | 'trial';
export type HoldingsBillingCycle = 'monthly' | 'quarterly' | 'annual' | 'one_time';
export type HoldingsBankAccountType = 'checking' | 'savings' | 'credit_card' | 'investment' | 'other';
export type HoldingsTransactionMatchStatus = 'matched' | 'unmatched' | 'ignored' | 'manual' | 'pending_review';
export type HoldingsTransactionReviewStatus = 'pending' | 'reviewed' | 'flagged';
export type HoldingsPlanStatus = 'draft' | 'approved' | 'executed' | 'archived';
export type HoldingsPlanLineItemType = 'new_subscription' | 'modify_subscription' | 'cancel_subscription' | 'headcount_change' | 'one_time_expense';
export type HoldingsRevenuePeriodType = 'month' | 'quarter';
export type HoldingsAccessLevel = 'viewer' | 'editor' | 'admin';

// Alert types for smart matching
export type HoldingsAlertType = 'price_change' | 'new_recurring' | 'missing_payment' | 'match_review';
export type HoldingsAlertSeverity = 'info' | 'warning' | 'critical';
export type HoldingsTransactionClassification = 'subscription_payment' | 'likely_subscription' | 'one_time_expense' | 'pending_review';
export type HoldingsMatchConfidenceLevel = 'exact' | 'high' | 'medium' | 'low';

export interface HoldingsCompany {
  id: string;
  name: string;
  status: HoldingsCompanyStatus;
  ownershipStakePercentage?: number;
  description?: string;
  website?: string;
  logoUrl?: string;
  industry?: string;
  foundedDate?: string;
  acquisitionDate?: string;
  exitDate?: string;
  notes?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  // Joined data
  people?: HoldingsCompanyPerson[];
  subscriptionSeats?: HoldingsSubscriptionSeat[];
  oneTimeCosts?: HoldingsOneTimeCost[];
}

export interface HoldingsPerson {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  role?: string;
  notes?: string;
  isActive: boolean;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  // Joined data
  companyAssignments?: HoldingsCompanyPerson[];
  subscriptionSeats?: HoldingsSubscriptionSeat[];
  compensation?: import('./types/compensation').HoldingsCompensation;
}

export interface HoldingsCompanyPerson {
  id: string;
  companyId: string;
  personId: string;
  roleAtCompany?: string;
  startDate?: string;
  endDate?: string;
  isPrimaryContact: boolean;
  createdAt: string;
  // Joined data
  company?: HoldingsCompany;
  person?: HoldingsPerson;
}

export interface HoldingsSubscription {
  id: string;
  vendorName: string;
  description?: string;
  costCents: number;
  billingCycle: HoldingsBillingCycle;
  renewalDate?: string;
  startDate?: string;
  totalSeats: number;
  category?: string;
  status: HoldingsSubscriptionStatus;
  autoRenew: boolean;
  contractUrl?: string;
  notes?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  // Smart matching fields
  lastChargedAmountCents?: number;
  lastChargedDate?: string;
  vendorAliases?: string[];
  expectedDayOfMonth?: number;
  amountTolerancePercent?: number;
  // Computed fields
  assignedSeats?: number;
  unassignedSeats?: number;
  hasAlerts?: boolean;
  alertCount?: number;
  // Joined data
  seats?: HoldingsSubscriptionSeat[];
  history?: HoldingsSubscriptionHistory[];
  alerts?: HoldingsAlert[];
}

export interface HoldingsSubscriptionSeat {
  id: string;
  subscriptionId: string;
  companyId?: string;
  personId?: string;
  seatsAssigned: number;
  notes?: string;
  assignedAt: string;
  // Joined data
  subscription?: HoldingsSubscription;
  company?: HoldingsCompany;
  person?: HoldingsPerson;
}

export interface HoldingsSubscriptionHistory {
  id: string;
  subscriptionId: string;
  costCents: number;
  billingCycle: HoldingsBillingCycle;
  totalSeats?: number;
  effectiveDate: string;
  changeReason?: string;
  createdAt: string;
}

export interface HoldingsOneTimeCost {
  id: string;
  date: string;
  amountCents: number;
  category: string;
  description: string;
  vendorName?: string;
  companyId?: string;
  personId?: string;
  receiptUrl?: string;
  notes?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  // Joined data
  company?: HoldingsCompany;
  person?: HoldingsPerson;
}

export interface HoldingsCostCategory {
  id: string;
  name: string;
  description?: string;
  color?: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
}

export interface HoldingsBankAccount {
  id: string;
  name: string;
  accountType: HoldingsBankAccountType;
  lastFour?: string;
  bankName?: string;
  isActive: boolean;
  notes?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface HoldingsStatementImport {
  id: string;
  bankAccountId?: string;
  fileName: string;
  fileType: 'csv' | 'pdf';
  fileUrl?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  totalTransactions: number;
  matchedTransactions: number;
  unmatchedTransactions: number;
  errorMessage?: string;
  processedAt?: string;
  createdBy?: string;
  createdAt: string;
  // Joined data
  bankAccount?: HoldingsBankAccount;
}

export interface HoldingsTransaction {
  id: string;
  importId?: string;
  bankAccountId?: string;
  transactionDate: string;
  amountCents: number;
  description: string;
  vendorNameRaw?: string;
  vendorNameNormalized?: string;
  category?: string;
  isRecurring: boolean;
  recurrencePattern?: string;
  matchedSubscriptionId?: string;
  matchedOneTimeCostId?: string;
  matchStatus: HoldingsTransactionMatchStatus;
  matchConfidence?: number;
  reviewStatus: HoldingsTransactionReviewStatus;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  // Smart matching fields
  expectedAmountCents?: number;
  amountVarianceCents?: number;
  classification?: HoldingsTransactionClassification;
  // Joined data
  matchedSubscription?: HoldingsSubscription;
  matchedOneTimeCost?: HoldingsOneTimeCost;
}

export interface HoldingsVendorMapping {
  id: string;
  rawName: string;
  normalizedName: string;
  subscriptionId?: string;
  isRecurring: boolean;
  createdAt: string;
}

export interface HoldingsAlert {
  id: string;
  alertType: HoldingsAlertType;
  severity: HoldingsAlertSeverity;
  title: string;
  description?: string;
  subscriptionId?: string;
  transactionId?: string;
  expectedAmountCents?: number;
  actualAmountCents?: number;
  isResolved: boolean;
  resolvedBy?: string;
  resolvedAt?: string;
  resolutionAction?: string;
  createdAt: string;
  // Joined data
  subscription?: HoldingsSubscription;
  transaction?: HoldingsTransaction;
}

export interface HoldingsMatchSuggestion {
  subscriptionId: string;
  vendorName: string;
  expectedCostCents: number;
  score: number;
  confidenceLevel: HoldingsMatchConfidenceLevel;
  vendorScore: number;
  amountScore: number;
  varianceCents: number;
  needsReview: boolean;
  hasPriceChange: boolean;
}

export interface HoldingsAlertCounts {
  total: number;
  price_change: number;
  new_recurring: number;
  missing_payment: number;
  match_review: number;
}

export interface HoldingsCostPlan {
  id: string;
  name: string;
  description?: string;
  timeHorizonMonths: number;
  startDate: string;
  endDate?: string;
  status: HoldingsPlanStatus;
  approvedBy?: string;
  approvedAt?: string;
  executedAt?: string;
  notes?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  // Computed fields
  totalMonthlyImpact?: number;
  totalAnnualImpact?: number;
  lineItemCount?: number;
  // Joined data
  lineItems?: HoldingsPlanLineItem[];
}

export interface HoldingsPlanLineItem {
  id: string;
  planId: string;
  itemType: HoldingsPlanLineItemType;
  name: string;
  description?: string;
  subscriptionId?: string;
  newCostCents?: number;
  newBillingCycle?: HoldingsBillingCycle;
  seatChange: number;
  personName?: string;
  role?: string;
  salaryCents?: number;
  headcountDelta: number;
  expenseDate?: string;
  expenseCategory?: string;
  companyId?: string;
  effectiveDate: string;
  monthlyImpactCents: number;
  annualImpactCents: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  // Joined data
  subscription?: HoldingsSubscription;
  company?: HoldingsCompany;
}

export interface HoldingsRevenue {
  id: string;
  companyId: string;
  periodType: HoldingsRevenuePeriodType;
  periodStart: string;
  periodEnd: string;
  revenueCents: number;
  notes?: string;
  source: 'manual' | 'import' | 'api';
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  // Joined data
  company?: HoldingsCompany;
}

export interface HoldingsPermission {
  id: string;
  userId: string;
  accessLevel: HoldingsAccessLevel;
  createdAt: string;
  // Joined data
  profile?: {
    email: string;
    name: string;
  };
}

// Analytics types
export interface HoldingsRunRate {
  monthlyRunRateCents: number;
  annualRunRateCents: number;
  activeSubscriptions: number;
}

export interface HoldingsCostsByCompany {
  companyId: string;
  companyName: string;
  status: HoldingsCompanyStatus;
  monthlySubscriptionCostCents: number;
  oneTimeCosts30dCents: number;
  totalSeats: number;
}

export interface HoldingsCostsByCategory {
  category: string;
  monthlySubscriptionCostCents: number;
  oneTimeCostsYearCents: number;
}

export interface HoldingsSummary {
  totalCompanies: number;
  activeCompanies: number;
  totalPeople: number;
  activePeople: number;
  totalSubscriptions: number;
  activeSubscriptions: number;
  totalSeats: number;
  monthlyRunRateCents: number;
  annualRunRateCents: number;
}

// Display configuration
export const HOLDINGS_COMPANY_STATUSES: { status: HoldingsCompanyStatus; label: string; color: string }[] = [
  { status: 'active', label: 'Active', color: 'bg-emerald-100 text-emerald-700' },
  { status: 'inactive', label: 'Inactive', color: 'bg-slate-100 text-slate-600' },
  { status: 'sold', label: 'Sold', color: 'bg-purple-100 text-purple-700' },
];

export const HOLDINGS_SUBSCRIPTION_STATUSES: { status: HoldingsSubscriptionStatus; label: string; color: string }[] = [
  { status: 'active', label: 'Active', color: 'bg-emerald-100 text-emerald-700' },
  { status: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' },
  { status: 'pending', label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  { status: 'trial', label: 'Trial', color: 'bg-blue-100 text-blue-700' },
];

export const HOLDINGS_BILLING_CYCLES: { cycle: HoldingsBillingCycle; label: string; multiplier: number }[] = [
  { cycle: 'monthly', label: 'Monthly', multiplier: 12 },
  { cycle: 'quarterly', label: 'Quarterly', multiplier: 4 },
  { cycle: 'annual', label: 'Annual', multiplier: 1 },
  { cycle: 'one_time', label: 'One-time', multiplier: 0 },
];

export const HOLDINGS_PLAN_STATUSES: { status: HoldingsPlanStatus; label: string; color: string }[] = [
  { status: 'draft', label: 'Draft', color: 'bg-slate-100 text-slate-600' },
  { status: 'approved', label: 'Approved', color: 'bg-blue-100 text-blue-700' },
  { status: 'executed', label: 'Executed', color: 'bg-emerald-100 text-emerald-700' },
  { status: 'archived', label: 'Archived', color: 'bg-neutral-100 text-neutral-500' },
];

// Helper function to format cents as currency
export const formatCents = (cents: number, currency = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(cents / 100);
};

// Helper function to calculate monthly cost from any billing cycle
export const getMonthlyEquivalent = (costCents: number, billingCycle: HoldingsBillingCycle): number => {
  switch (billingCycle) {
    case 'monthly': return costCents;
    case 'quarterly': return costCents / 3;
    case 'annual': return costCents / 12;
    case 'one_time': return 0;
    default: return costCents;
  }
};

// Helper function to calculate annual cost from any billing cycle
export const getAnnualEquivalent = (costCents: number, billingCycle: HoldingsBillingCycle): number => {
  switch (billingCycle) {
    case 'monthly': return costCents * 12;
    case 'quarterly': return costCents * 4;
    case 'annual': return costCents;
    case 'one_time': return costCents;
    default: return costCents;
  }
};
